// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    vec2,
    vec3,
} from 'gl-matrix';
import {
    dcmjsGetString,
    dicomSlicePixelDataToFloat32,
    dicomSlicePixelDataToInternal,
} from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { WindowLevel } from '../patient/windowLevel';
import { PetSeriesMetadata } from '../patient/petSuv';
import { Modality } from './modality';

export class DicomImageSlice extends DicomBase {
    /**
     * Physical distance in between the center of each pixel, specified by a numeric pair.\
     * The pixelSpacing in planar directions should stay constant between slices.
     * (0028,0030) https://dicom.innolitics.com/ciods/ct-image/image-plane/00280030
     */
    public readonly pixelSpacing: ReadonlyVec2;

    /**
     * The x, y, and z coordinates of the upper left hand corner (center of the first voxel transmitted) of the image, in mm.\
     * These are the PatientSpace coordinates.\
     * Note the coordinates are for the CENTER of the first voxel.\
     * So half a pixel offset is often needed to get more meaningful coordinates.
     * (0020,0032) https://dicom.innolitics.com/ciods/ct-image/image-plane/00200032
     */
    public readonly imagePosition: ReadonlyVec3;

    /**
     * The thickness of the slice in mm.\
     * (0018,0050) https://dicom.innolitics.com/ciods/ct-image/image-plane/00180050
     */
    public readonly sliceThickness: number;

    /**
     * This is the rotation of the Image in PatientSpace.\
     * By applying this matrix to a vertex, it can be converted from ImageSpace to PatientSpace.
     * (0020,0037) https://dicom.innolitics.com/ciods/ct-image/image-plane/00200037
     */
    public readonly imageOrientation: ReadonlyMat3;

    /**
     * A description of the Series.\
     * (0008,103e) https://dicom.innolitics.com/ciods/ct-image/general-series/0008103e
     */
    public readonly seriesDescription: string;

    /**
     * The Series Instance UID.\
     * (0020,000e) https://dicom.innolitics.com/ciods/ct-image/general-series/0020000e
     */
    public readonly seriesInstanceUid: string;

    /**
     * The Frame of Reference UID.\
     * (0020,0052) https://dicom.innolitics.com/ciods/ct-image/general-image/00200052
     */
    public readonly frameOfReferenceUid: string;

    /**
     * The manufacturer of the equipment that produced the image.\
     * (0008,0070) https://dicom.innolitics.com/ciods/ct-image/general-image/00080070
     */
    public readonly manufacturer: string;

    /**
     * The manufacturer's model name of the equipment that produced the image.\
     * (0008,1090) https://dicom.innolitics.com/ciods/ct-image/general-equipment/00081090
     */
    public readonly manufacturerModelName: string;

    /**
     * The instance number of the image.\
     * (0020,0013) https://dicom.innolitics.com/ciods/ct-image/general-image/00200013
     */
    public readonly instanceNumber: number;

    /**
     * Image identification characteristics, ['ORIGINAL', 'PRIMARY'] etc.\
     * 	(0008,0008) https://dicom.innolitics.com/ciods/ct-image/general-image/00080008
     */
    public readonly imageType: string[];

    /**
     * Number of pixels in image's vertical direction.\
     * This is the {@link Image image}'s pixel size along y-axis.\
     * (0028, 0010) https://dicom.innolitics.com/ciods/ct-image/image-pixel/00280010
     */
    public readonly rows: number;

    /**
     * Number of pixels in image's horizontal direction.\
     * This is the {@link Image image}'s pixel size along x-axis.\
     * (0028, 0011) https://dicom.innolitics.com/ciods/ct-image/image-pixel/00280011
     */
    public readonly cols: number;

    /**
     * Patient position during imaging.\
     * (0018,5100) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00185100
     */
    public readonly patientPosition: string;

    /**
     * Name of the station that produced the image.\
     * (0008,1010) https://dicom.innolitics.com/ciods/ct-image/general-equipment/00081010
     */
    public readonly stationName: string;

    /**
     * The type of filter used in the image.\
     * (0018,1160) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00181160
     */
    public readonly filterType: string;

    /**
     * The intercept of the linear relationship between the stored values in the image and the pixel values in image output units.\
     * (0028,1052) https://dicom.innolitics.com/ciods/ct-image/ct-image/00281052
     */
    public readonly intercept: number = 0;

    /**
     * The slope of the linear relationship between the stored values in the image and the pixel values in image output units.\
     * (0028,1052) https://dicom.innolitics.com/ciods/ct-image/ct-image/00281052
     */
    public readonly slope: number = 1.0;

    /**
     * The peak kilovoltage output of the X-ray generator used.\
     * (0018,0060) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180060
     */

    public readonly kvp?: string;

    /**
     * The computed tomography dose index.\
     * (0018,0093) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180093
     */

    public readonly ctdi?: string;

    /**
     * The convolution kernel used for reconstruction.\
     * (0018,9311) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00189311
     */

    public readonly kernel?: string;

    /**
     * The sequence of the scanning sequence.\
     * (0018,0020) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180020
     */

    public readonly scanningSequence?: string;

    /**
     * The type of MRA acquisition.\
     * (0018,0023) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180023
     */

    public readonly mraAcquisitionType?: string;

    /**
     * The sequence variant.\
     * (0018,0024) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00180024
     */

    public readonly sequenceVariant?: string;

    /**
     * The protocol name.\
     * (0018,1030) https://dicom.innolitics.com/ciods/ct-image/ct-acquisition/00181030
     */

    public readonly protocolName?: string;

    /** Patient weight in kg (0010,1030) */
    public readonly patientWeightKg?: number;

    /**
     * Radionuclide total dose injected in Bq.
     * (0054,0016)[0].(0018,1074)
     */
    public readonly radionuclideTotalDose?: number;

    /**
     * Radionuclide half life in seconds.
     * (0054,0016)[0].(0018,1075)
     */
    public readonly radionuclideHalfLife?: number;

    /**
     * Radiopharmaceutical start time (injection time) as DICOM time string.
     * (0054,0016)[0].(0018,1072)
     */
    public readonly radiopharmaceuticalStartTime?: string;

    /**
     * Radiopharmaceutical start datetime as DICOM datetime string.
     * (0054,0016)[0].(0018,1078)
     */
    public readonly radiopharmaceuticalStartDateTime?: string;

    /**
     * Series time as DICOM time string.
     * (0008,0031)
     */
    public readonly seriesTime?: string;

    /**
     * Acquisition time as DICOM time string.
     * (0008,0032)
     */
    public readonly acquisitionTime?: string;

    /** Acquisition date (0008,0022) */
    public readonly acquisitionDate?: string;

    /** Series date (0008,0021) */
    public readonly seriesDate?: string;

    /** Study date (0008,0020) */
    public readonly studyDate?: string;

    /**
     * The pixel data of the image, converted to external values by using intercept and slope.\
     * (7fe0,0010) https://dicom.innolitics.com/ciods/ct-image/pixel-data/7fe00010
     *
     * Not available for PET images — rescaled values can exceed Int16 range.
     * Use {@link floatPixelData} instead for PET.
     */
    private _pixelData?: Int16Array;
    public get pixelData(): Int16Array {
        if (this.modality === Modality.PT) {
            throw new Error(
                'Int16 pixel data is not available for PET images — ' +
                    'rescaled values exceed Int16 range. ' +
                    'Use floatPixelData.'
            );
        }
        if (!this._pixelData) {
            this._pixelData = this.toInternalPixelData();
        }
        return this._pixelData;
    }

    /**
     * Float32 pixel data with rescale slope/intercept applied.
     * Safe for all modalities including PET where values exceed Int16 range.
     */
    private _floatPixelData?: Float32Array;
    public get floatPixelData(): Float32Array {
        if (!this._floatPixelData) {
            this._floatPixelData = this.toFloat32PixelData();
        }
        return this._floatPixelData;
    }

    /**
     * Whether the patient is in head first prone position.\
     */
    public readonly headFirstProne: boolean;

    /**
     * Whether the dataset is in a tilted position.\
     */
    public readonly tilted: boolean;

    private _windowLevel: WindowLevel | undefined;

    public get windowLevel(): WindowLevel | undefined {
        return this._windowLevel;
    }

    public set windowLevel(windowLevel: WindowLevel | undefined) {
        this._windowLevel = windowLevel;
    }

    constructor(
        sopUid: string,
        modality: string,
        pixelSpacing: vec2,
        imagePosition: vec3,
        imageOrientation: mat3,
        sliceThickness: number,
        seriesDescription: string,
        seriesInstanceUid: string,
        frameOfReferenceUid: string,
        manufacturer: string,
        manufacturerModelName: string,
        instanceNumber: number,
        imageType: string[],
        rows: number,
        cols: number,
        patientPosition: string,
        stationName: string,
        filterType: string,
        intercept: number,
        slope: number,
        headFirstProne: boolean,
        tilted: boolean,
        dataset: DcmJsDatasetWrapper,
        patientWeightKg?: number,
        seriesTime?: string,
        acquisitionTime?: string,
        studyDate?: string,
        seriesDate?: string,
        acquisitionDate?: string,
        kvp?: string,
        ctdi?: string,
        kernel?: string,
        protocolName?: string,
        scanningSequence?: string,
        mraAcquisitionType?: string,
        sequenceVariant?: string,
        radionuclideTotalDose?: number,
        radionuclideHalfLife?: number,
        radiopharmaceuticalStartTime?: string,
        radiopharmaceuticalStartDateTime?: string
    ) {
        super(sopUid, modality, dataset);
        this.pixelSpacing = pixelSpacing;
        this.imagePosition = imagePosition;
        this.imageOrientation = imageOrientation;
        this.seriesDescription = seriesDescription;
        this.sliceThickness = sliceThickness;
        this.seriesDescription = seriesDescription;
        this.seriesInstanceUid = seriesInstanceUid;
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.manufacturer = manufacturer;
        this.manufacturerModelName = manufacturerModelName;
        this.instanceNumber = instanceNumber;
        this.imageType = imageType;
        this.rows = rows;
        this.cols = cols;
        this.patientPosition = patientPosition;
        this.stationName = stationName;
        this.filterType = filterType;
        this.intercept = intercept;
        this.slope = slope;
        this.headFirstProne = headFirstProne;
        this.tilted = tilted;
        this.kvp = kvp;
        this.ctdi = ctdi;
        this.kernel = kernel;
        this.scanningSequence = scanningSequence;
        this.mraAcquisitionType = mraAcquisitionType;
        this.sequenceVariant = sequenceVariant;
        this.protocolName = protocolName;
        this.patientWeightKg = patientWeightKg;
        this.radionuclideTotalDose = radionuclideTotalDose;
        this.radionuclideHalfLife = radionuclideHalfLife;
        this.radiopharmaceuticalStartTime = radiopharmaceuticalStartTime;
        this.radiopharmaceuticalStartDateTime =
            radiopharmaceuticalStartDateTime;
        this.seriesTime = seriesTime;
        this.acquisitionTime = acquisitionTime;
        this.studyDate = studyDate;
        this.seriesDate = seriesDate;
        this.acquisitionDate = acquisitionDate;
    }

    public get studyInstanceUID(): string {
        return dcmjsGetString(this.datasetWrapper, 'StudyInstanceUID');
    }

    /** Resolves the best available scan datetime as a DICOM DateTime string.\
     * Prefers seriesDate + seriesTime, then acquisitionDate + acquisitionTime, then studyDate + acquisitionTime, then studyDate + seriesTime.
     * */
    public get resolvedScanDateTime(): string | undefined {
        if (this.seriesDate && this.seriesTime) {
            return this.seriesDate + this.seriesTime;
        }
        if (this.acquisitionDate && this.acquisitionTime) {
            return this.acquisitionDate + this.acquisitionTime;
        }
        if (this.acquisitionTime && (this.studyDate ?? this.seriesDate)) {
            return (this.studyDate ?? this.seriesDate) + this.acquisitionTime;
        }
        if (this.seriesTime && this.studyDate) {
            return this.studyDate + this.seriesTime;
        }
        return undefined;
    }

    /**
     * Resolves the best available injection datetime as a DICOM DateTime string.
     * Prefers radiopharmaceuticalStartDateTime (already date+time),
     * falls back to studyDate + radiopharmaceuticalStartTime.
     */
    public get resolvedInjectionDateTime(): string | undefined {
        if (this.radiopharmaceuticalStartDateTime) {
            return this.radiopharmaceuticalStartDateTime;
        }
        if (
            this.radiopharmaceuticalStartTime &&
            (this.studyDate ?? this.acquisitionDate ?? this.seriesDate)
        ) {
            const date =
                this.studyDate ?? this.acquisitionDate ?? this.seriesDate;
            return date + this.radiopharmaceuticalStartTime;
        }
        return undefined;
    }

    /**
     * Returns resolved PET metadata with single datetime fields.
     */
    public get petMetadata(): PetSeriesMetadata | null {
        if (this.modality !== Modality.PT) {
            return null;
        }
        return {
            patientWeightKg: this.patientWeightKg,
            radionuclideTotalDose: this.radionuclideTotalDose,
            radionuclideHalfLife: this.radionuclideHalfLife,
            scanDateTime: this.resolvedScanDateTime,
            injectionDateTime: this.resolvedInjectionDateTime,
        };
    }

    /**
     * Extract the raw DICOM pixel ArrayBuffer, handling dcmjs 0.19.6 bug
     * where naturalized array buffers are wrapped in an array.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getRawPixelArrayBuffer(): [ArrayBuffer, any] {
        /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataset = this.datasetWrapper.dataset as any;
        // dcmjs 0.19.6 has a bug where naturalized array buffers (such as PixelData) are unnecessarily put inside an array -- the following line
        // works with both the original (and hopefully future fixed case) where array buffers are NOT inside an array, and the current 0.19.6
        // case where they are inside an array (which has a 'length' prop)
        // TODO: revert this workaround once dcmjs has been patched
        const arraybuf: ArrayBuffer = Object.prototype.hasOwnProperty.call(
            dataset.PixelData,
            'length'
        )
            ? dataset.PixelData[0]
            : dataset.PixelData;
        return [arraybuf, dataset];
        /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
    }

    toInternalPixelData(): Int16Array {
        /* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
        const [arraybuf, dataset] = this.getRawPixelArrayBuffer();
        return dicomSlicePixelDataToInternal(
            dataset.PixelRepresentation,
            arraybuf,
            dataset.BitsAllocated,
            dataset.BitsStored,
            this.slope,
            this.intercept
        );
        /* eslint-enable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
    }

    toFloat32PixelData(): Float32Array {
        /* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
        const [arraybuf, dataset] = this.getRawPixelArrayBuffer();
        return dicomSlicePixelDataToFloat32(
            dataset.PixelRepresentation,
            arraybuf,
            dataset.BitsAllocated,
            dataset.BitsStored,
            this.slope,
            this.intercept
        );
        /* eslint-enable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access */
    }
}
